import { IChatWidget } from "./types/ChatWidget";
import { useCallback, memo, forwardRef } from "react";
import { ChatWidgetRendererContainer } from "./styles";
import { ErrorWidget } from "./errors/ErrorWidget";
import { CmaDetailsWidget } from "./cma/CmaDetailsWidget";
import { ActiveWidgetContext } from "@cma/store/chat-widget.store";
import { ListingDetailsWidget } from "./listing-details/ListingDetailsWidget";
import { ChatWidgetConfirmDialog } from "@cma/shared/components/ChatWidgetConfirmDialog";
import { ErrorBoundaryProvider } from "@shared/components/ErrorBoundaryProvider";
import { FindListingsSearchWidgetV2 } from "./find-listings-search/FindListingsSearchWidgetV2";

interface Props {
  widget: IChatWidget | null;
  isLoading: boolean;
  setChatWidgetContext: (context: ActiveWidgetContext) => void;
  setChatWidget: (widget: IChatWidget) => void;
  resetChatWidget: () => void;
}

export const ChatWidgetRenderer = memo(
  forwardRef<HTMLDivElement, Props>((props, ref) => {
    const {
      widget,
      isLoading,
      setChatWidgetContext,
      setChatWidget,
      resetChatWidget,
    } = props;

    const renderWidget = useCallback(() => {
      if (!widget) return null;

      switch (widget.kind) {
        case "widget-listings-search": {
          return (
            <FindListingsSearchWidgetV2
              key={`${widget.kind}-${widget.artifactId}`}
              widget={widget}
              setChatWidgetContext={setChatWidgetContext}
              setChatWidget={setChatWidget}
            />
          );
        }

        case "widget-cma-details": {
          return (
            <CmaDetailsWidget
              key={`${widget.kind}-${widget.content.cmaId}`}
              widget={widget}
              setChatWidget={setChatWidget}
              setChatWidgetContext={setChatWidgetContext}
            />
          );
        }

        case "widget-listing-details": {
          return (
            <ListingDetailsWidget
              key={`${widget.kind}-${widget.listingReference?.mlsId}:${widget.listingReference?.mlsSource}`}
              widget={widget}
            />
          );
        }

        case "widget-error": {
          return <ErrorWidget key={widget.kind} widget={widget} />;
        }

        default: {
          return (
            <ErrorWidget
              key={"widget-error"}
              widget={{
                kind: "widget-error",
                title: "Error",
                artifactId: null,
                errorMessage: `Widget with kind ${(widget as IChatWidget).kind} is not supported.`,
              }}
            />
          );
        }
      }
    }, [setChatWidgetContext, setChatWidget, widget]);

    return (
      <ErrorBoundaryProvider onResetError={resetChatWidget}>
        <ChatWidgetRendererContainer ref={ref} aria-disabled={isLoading}>
          {renderWidget()}
          <ChatWidgetConfirmDialog />
        </ChatWidgetRendererContainer>
      </ErrorBoundaryProvider>
    );
  }),
);

ChatWidgetRenderer.displayName = "ChatWidgetRenderer";
